// src/pages/SingleListingGenerator/SingleListingGeneratorPage.js
import React, { useEffect, useContext, useRef, useCallback, useMemo, startTransition , useState} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './SingleListingGeneratorPage.module.css';
import { FaArrowLeft } from "react-icons/fa6";
import AuthContext from '../../context/AuthContext';
import { useFormStep } from '../../context/FormStepContext';
import { SingleListingFormProvider } from '../../context/SingleListingFormContext';
import SingleResultsCard from '../../components/Generator/SingleResultsCard';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import StepNavigationTabs from './components/StepNavigationTabs';
import FormSteps from './components/FormSteps';
import TutorialTooltip from '../../components/UI/TutorialTooltip';
import TutorialSampleCards from '../../components/UI/TutorialSampleCards';
import { BgReminderModal } from './components/modals';
import { useTutorial } from '../../context/TutorialContext';
import useSingleFlight from '../../hooks/useSingleFlight';
import sampleAsins from '../../assets/tutorial/sample_asins.json';



// Constants, Sample Data, Utils
import {
  initialNewFormData,
  initialExistingInput,
  MAX_IMAGES,
  THEME_CONFIG
} from './constants';
import { API_BASE_URL, SINGLE_GEN_COST } from './constants';
import themeSlideCounts from '../../assets/theme_slide_counts.json';
import {
  prepareSubmissionData,
  validateFormData,
  handleApiError,
  mapRainforestDataToFormFields,
  mapSpecificationsToDetails,
  fetchVariantEnumsFromBrowseNode
} from './utils';
import { triggerLowCreditsModal, hasEnoughCreditsOrPrompt } from '../../utils/lowCreditsModal';
import {
  useSingleListingFormState,
  useSingleListingAsinState,
  useSingleListingImageState,
  useSingleListingDraftState,
  useSingleListingSubmissionState,
  useSingleListingVariantsState,
  useSingleListingUIState
} from './hooks/useSingleListingState';

// Optional PostHog: load only if available so the page works without it
let posthog = null;
import('posthog-js').then((m) => { posthog = m.default; }).catch(() => {});

const SingleListingGeneratorPage = () => {
  // --- Context & Navigation ---
  const authCtx = useContext(AuthContext);
  const { setFormStep: setContextFormStep, setListingMode: setContextListingMode, setShowVariantsTab: setContextShowVariantsTab, isThemesPanelOpen, setIsThemesPanelOpen, setSelectedTheme: setContextSelectedTheme, setHandleThemeSelect: setContextHandleThemeSelect, setShowResultPopup: setContextShowResultPopup, isFontsPanelOpen, selectedFonts } = useFormStep();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const disableAsinAutoFetch = Boolean(location.state?.listingUpgradePayload);

  // --- State (grouped via custom hooks to reduce 47+ useState declarations) ---
  const {
    listingMode,
    setListingMode,
    formData,
    setFormData,
    existingInput,
    setExistingInput,
    dimensionData,
    setDimensionData,
    formStep,
    setFormStep,
    showVariantsTab,
    setShowVariantsTab,
    invalidFields,
    setInvalidFields,
    currentAsinValue,
    setCurrentAsinValue,
    asinResetKey,
    setAsinResetKey,
    listingUpgradePayload,
    setListingUpgradePayload
  } = useSingleListingFormState(initialNewFormData, initialExistingInput);

  const {
    isAsinDataFetched,
    setIsAsinDataFetched,
    asinProductData,
    setAsinProductData,
    isAsinLookupLoading,
    setIsAsinLookupLoading,
    shouldAdvanceAfterAsinFetch,
    setShouldAdvanceAfterAsinFetch
  } = useSingleListingAsinState();

  const {
    uploadedImages,
    setUploadedImages,
    bPlusSequence,
    setBPlusSequence,
    aPlusSequence,
    setAPlusSequence,
    zoomAreas,
    setZoomAreas,
    singleImage,
    setSingleImage,
    productImages,
    setProductImages,
    selectedTemplates,
    setSelectedTemplates,
    selectedTemplatesWithImages,
    setSelectedTemplatesWithImages,
    currentCarouselStep,
    setCurrentCarouselStep,
    singleImageError,
    setSingleImageError,
    productIdentityImageError,
    setProductIdentityImageError,
    imageError,
    setImageError,
    draggedImageIndex,
    setDraggedImageIndex,
    selectedAPlusTemplate,
    setSelectedAPlusTemplate,
    aplusTemplateImage,
    setAPlusTemplateImage,
    aplusImageError,
    setAPlusImageError
  } = useSingleListingImageState();

  const {
    showDraftModal,
    setShowDraftModal,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    navigationBlocked,
    setNavigationBlocked,
    currentDraftId,
    setCurrentDraftId,
    isDraftMode,
    setIsDraftMode,
    pendingNavigation,
    setPendingNavigation,
  } = useSingleListingDraftState();

  const {
    isLoading,
    setIsLoading,
    error,
    setError,
    generatedResult,
    setGeneratedResult,
    showResultPopup,
    setShowResultPopup,
    countError,
    setCountError,
    keyFeaturesError,
    setKeyFeaturesError
  } = useSingleListingSubmissionState();

  const {
    variantsDraftData,
    setVariantsDraftData,
    isVariantsValid,
    setIsVariantsValid,
    variantsInternalStep,
    setVariantsInternalStep
  } = useSingleListingVariantsState();

  const {
    showTutorialStep,
    setShowTutorialStep,
    selectedTheme,
    setSelectedTheme
  } = useSingleListingUIState();

  const generateButtonRef = useRef(null);
  const asinLookupFormRef = useRef(null);
  const asinInputElementRef = useRef(null);
  const formStepNextButtonRef = useRef(null);
  const skuIdFieldRef = useRef(null);
  const keywordsFieldRef = useRef(null);
  const bannedKeywordsFieldRef = useRef(null);
  const briefDescriptionRef = useRef(null);
  const keyFeaturesRef = useRef(null);
  const howIsUsedRef = useRef(null);
  const productDimensionsRef = useRef(null);
  const productIdentityRef = useRef(null);
  const formRef = useRef(null);
  const asinFetchBulletsEmpty = useRef(false);
  const asinFetchDescriptionEmpty = useRef(false);
  // Refs that always hold the latest sequence values so the unmount cleanup
  // below can revoke blob URLs even though it runs with [] deps.
  const bPlusSequenceRef = useRef(bPlusSequence);
  const aPlusSequenceRef = useRef(aPlusSequence);
  const [asinBulletsLocked, setAsinBulletsLocked] = useState(false);
  const [asinDescriptionLocked, setAsinDescriptionLocked] = useState(false);
  // Pre-generation "remove background" reminder. The bypass ref lets a user who
  // chose "Continue anyway" generate without being prompted again. The signal
  // counter tells ImageUploadStep to open its Remove Background modal.
  const [showBgReminder, setShowBgReminder] = useState(false);
  const [openRemoveBgSignal, setOpenRemoveBgSignal] = useState(0);
  const bgReminderBypassRef = useRef(false);
  // Tracks real unmount so long-running async work (e.g. upgrade-payload image
  // hydration) isn't aborted by benign effect re-runs (e.g. location.state churn
  // from the query-param sync). Only flips false when the component truly unmounts.
  const isMountedRef = useRef(true);
  // Drives the post-"Add Sample Details" transition on step 7:
  // 'idle' → both SKU + keywords tooltips shown (default)
  // 'pause' → tooltips hidden so user sees the just-filled chips (1s)
  // 'highlight' → spotlight on keywords only (3s)
  // After 'highlight' the tutorial advances to step 8 (Next button tooltip).
  const [tutorialFillPhase, setTutorialFillPhase] = useState('idle');

  // Sync formStep and listingMode with context for sidebar switching
  useEffect(() => {
    setContextFormStep(formStep);
    return () => {
      // Cleanup: reset context when component unmounts
      setContextFormStep(null);
    };
  }, [formStep, setContextFormStep]);

  useEffect(() => {
    setContextListingMode(listingMode);
    return () => {
      // Cleanup: reset context when component unmounts
      setContextListingMode(null);
    };
  }, [listingMode, setContextListingMode]);

  // Sync URL query param when listingMode changes
  useEffect(() => {
    const currentMode = searchParams.get('mode');
    if (currentMode !== listingMode) {
      setSearchParams({ mode: listingMode }, { replace: true });
    }
  }, [listingMode, searchParams, setSearchParams]);

  // Sync variants-tab visibility with context (DashboardLayout needs this for ThemeSidebar gating)
  useEffect(() => {
    setContextShowVariantsTab(showVariantsTab);
    return () => {
      setContextShowVariantsTab(false);
    };
  }, [showVariantsTab, setContextShowVariantsTab]);


  // Handle images from ProductIdentity and auto-populate to B+ and A+
  const handleProductIdentityImages = (images) => {
    // Always update to the new array from ProductIdentity
    // This ensures we have the latest images with valid blob URLs
    setBPlusSequence(images);
    setAPlusSequence(images);
    // Clear error when images are uploaded
    if (images.length > 0) {
      setProductIdentityImageError('');
    }
  };

  // Clear invalid fields when filled
  useEffect(() => {
    const findContainer = (element) => {
      let current = element?.parentElement;
      while (current) {
        const classList = Array.from(current.classList || []);
        if (classList.some(cls => cls.includes('inputContainer') || cls.includes('inputWrapper') || cls.includes('formGroup') || cls.includes('productTypeInputContainer') || cls.includes('dimensionInputContainer'))) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const root = formRef.current;
    if (!root) return;

    if (formData.brandName?.trim() && invalidFields.has('brandName')) {
      setInvalidFields(prev => { const next = new Set(prev); next.delete('brandName'); return next; });
      root.querySelectorAll('input[name="brandName"]').forEach(input => {
        const container = findContainer(input);
        container?.classList.remove(styles.fieldInvalid);
      });
    }
    if (formData.modelNumber?.trim() && invalidFields.has('modelNumber')) {
      setInvalidFields(prev => { const next = new Set(prev); next.delete('modelNumber'); return next; });
      root.querySelectorAll('input[name="modelNumber"]').forEach(input => {
        const container = findContainer(input);
        container?.classList.remove(styles.fieldInvalid);
      });
    }
    if (formData.skuId?.trim() && invalidFields.has('skuId')) {
      setInvalidFields(prev => { const next = new Set(prev); next.delete('skuId'); return next; });
      root.querySelectorAll('input[name="skuId"]').forEach(input => {
        const container = findContainer(input);
        container?.classList.remove(styles.fieldInvalid);
      });
    }
    if (formData.productType?.trim() && invalidFields.has('productType')) {
      setInvalidFields(prev => {
        const next = new Set(prev);
        next.delete('productType');
        return next;
      });
      const selectInput = root.querySelector('#productType-default');
      const container = findContainer(selectInput);
      container?.classList.remove(styles.fieldInvalid);
    }
    if (dimensionData?.length?.trim() && invalidFields.has('dimensionLength')) {
      setInvalidFields(prev => {
        const next = new Set(prev);
        next.delete('dimensionLength');
        return next;
      });
      root.querySelectorAll('input[name="dimensionWidth"]').forEach(input => {
        const container = findContainer(input);
        container?.classList.remove(styles.fieldInvalid);
      });
    }
    if (dimensionData?.breadth?.trim() && invalidFields.has('dimensionBreadth')) {
      setInvalidFields(prev => {
        const next = new Set(prev);
        next.delete('dimensionBreadth');
        return next;
      });
      root.querySelectorAll('input[name="dimensionLength"]').forEach(input => {
        const container = findContainer(input);
        container?.classList.remove(styles.fieldInvalid);
      });
    }
  }, [formData.brandName, formData.modelNumber, formData.skuId, formData.productType, dimensionData, invalidFields, styles]);

  // Apply invalid field highlighting to input containers
  useEffect(() => {
    const root = formRef.current;
    if (!root) return;

    const findContainer = (element) => {
      if (!element) return null;
      let current = element.parentElement;
      while (current) {
        const classList = Array.from(current.classList || []);
        if (classList.some(cls => cls.includes('inputContainer') || cls.includes('inputWrapper') || cls.includes('formGroup') || cls.includes('productTypeInputContainer') || cls.includes('dimensionInputContainer'))) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const applyHighlighting = () => {
      if (!formRef.current) return;
      const form = formRef.current;

      if (invalidFields.size === 0) {
        form.querySelectorAll(`.${styles.fieldInvalid}`).forEach(el => {
          el.classList.remove(styles.fieldInvalid);
        });
        return;
      }

      invalidFields.forEach(fieldName => {
        if (fieldName.startsWith('bullet-')) {
          form.querySelectorAll(`input[name="${fieldName}"]`).forEach(input => {
            const container = findContainer(input);
            container?.classList.add(styles.fieldInvalid);
          });
        } else if (fieldName === 'dimensionLength') {
          form.querySelectorAll('input[name="dimensionWidth"]').forEach(input => {
            const container = findContainer(input);
            container?.classList.add(styles.fieldInvalid);
          });
        } else if (fieldName === 'dimensionBreadth') {
          form.querySelectorAll('input[name="dimensionLength"]').forEach(input => {
            const container = findContainer(input);
            container?.classList.add(styles.fieldInvalid);
          });
        } else if (fieldName === 'productType') {
          const selectInput = form.querySelector('#productType-default');
          if (selectInput) {
            const container = findContainer(selectInput);
            container?.classList.add(styles.fieldInvalid);
          }
        } else if (fieldName === 'briefDescription') {
          form.querySelectorAll(`textarea[name="${fieldName}"]`).forEach(textarea => {
            const container = findContainer(textarea);
            container?.classList.add(styles.fieldInvalid);
          });
        } else {
          form.querySelectorAll(`input[name="${fieldName}"]`).forEach(input => {
            const container = findContainer(input);
            container?.classList.add(styles.fieldInvalid);
          });
        }
      });
    };

    const rafId = requestAnimationFrame(applyHighlighting);

    return () => {
      cancelAnimationFrame(rafId);
      if (formRef.current) {
        formRef.current.querySelectorAll(`.${styles.fieldInvalid}`).forEach(el => {
          el.classList.remove(styles.fieldInvalid);
        });
      }
    };
  }, [invalidFields, styles]);

  // Change detection - require at least one meaningful field to be filled (excluding just project name)
useEffect(() => {
  const hasFormData = 
    (formData.brandName?.trim() && formData.brandName.trim().length > 0) || 
    (formData.category?.trim() && formData.category.trim().length > 0) || 
    (formData.briefDescription?.trim() && formData.briefDescription.trim().length > 0) || 
    (formData.modelNumber?.trim() && formData.modelNumber.trim().length > 0) ||
    (formData.color?.trim() && formData.color.trim().length > 0) ||
    (formData.material?.trim() && formData.material.trim().length > 0) ||
    (formData.size?.trim() && formData.size.trim().length > 0) ||
    (formData.keywords?.trim() && formData.keywords.trim().length > 0) ||
    (formData.feature1?.trim() && formData.feature1.trim().length > 0) ||
    (formData.feature2?.trim() && formData.feature2.trim().length > 0) ||
    (formData.feature3?.trim() && formData.feature3.trim().length > 0) ||
    (formData.feature4?.trim() && formData.feature4.trim().length > 0) ||
    (formData.feature5?.trim() && formData.feature5.trim().length > 0) ||
    (existingInput.title?.trim() && existingInput.title.trim().length > 0) ||
    (existingInput.description?.trim() && existingInput.description.trim().length > 0) ||
    existingInput.bullets.some(bullet => bullet?.trim() && bullet.trim().length > 0) ||
    singleImage ||
    productImages.length > 0 ||
    selectedTemplatesWithImages.length > 0 ||
    selectedAPlusTemplate ||
    aPlusSequence.length > 0 ||
    (dimensionData && (dimensionData.length || dimensionData.breadth)) ||
    (variantsDraftData && Object.keys(variantsDraftData || {}).length > 0);
  
  setHasUnsavedChanges(hasFormData && !showResultPopup);
}, [
  formData, existingInput, singleImage,
  productImages, selectedTemplatesWithImages, selectedAPlusTemplate,
  aPlusSequence, dimensionData, variantsDraftData, showResultPopup, isDraftMode
]);

const handleAutoSaveDraft = useCallback(async () => {
  try {
    const productDataWithMode = {
      ...formData,
      is_existing: listingMode === 'existing'
    };

    // Collect image file names for restoration
    const imageNames = {
      bPlus: (bPlusSequence || []).map(img => img.name || img.file?.name || '').filter(Boolean),
      aPlus: (aPlusSequence || []).map(img => img.name || img.file?.name || '').filter(Boolean),
      dimensionImage: dimensionData?.image?.name || dimensionData?.image?.file?.name || null,
    };

    const draftPayload = {
      product: productDataWithMode,
      existingInput: existingInput,
      listingMode: listingMode,
      dimensionData: dimensionData ? {
        length: dimensionData.length,
        breadth: dimensionData.breadth,
        height: dimensionData.height,
        units: dimensionData.units,
        isComplete: !!dimensionData.isComplete,
      } : null,
      variantsData: variantsDraftData,
      imageNames: imageNames,
    };

    if (currentDraftId) {
      draftPayload.existingDraftId = currentDraftId;
    }

    // Check if we have any images to upload
    const allImages = [...(bPlusSequence || []), ...(aPlusSequence || [])];
    const dimImg = dimensionData?.image;
    if (dimImg?.file) allImages.push(dimImg);
    const hasImages = allImages.some(img => (img.file || img) instanceof File);

    if (hasImages) {
      // Use multipart/form-data with images ZIP
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      for (const img of (bPlusSequence || [])) {
        const file = img.file || img;
        const name = img.name || file?.name;
        if (file instanceof File && name) zip.file(`bPlus/${name}`, file);
      }
      for (const img of (aPlusSequence || [])) {
        const file = img.file || img;
        const name = img.name || file?.name;
        if (file instanceof File && name) zip.file(`aPlus/${name}`, file);
      }
      if (dimImg?.file instanceof File) {
        const name = dimImg.name || dimImg.file.name;
        if (name) zip.file(`dimension/${name}`, dimImg.file);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const apiFormData = new FormData();
      apiFormData.append('draftData', JSON.stringify(draftPayload));
      apiFormData.append('imagesZip', new File([zipBlob], 'draft-images.zip', { type: 'application/zip' }));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/single-gen/save-draft`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authCtx?.token}` },
        body: apiFormData,
      });

      if (response.ok) {
        const result = await response.json();
        setHasUnsavedChanges(false);
        setNavigationBlocked(false);
        if (result.action === "created") { setCurrentDraftId(result.draft_id); setIsDraftMode(true); }
        else if (result.action === "updated") { if (result.draft_id) setCurrentDraftId(result.draft_id); setIsDraftMode(true); }
        if (pendingNavigation) { pendingNavigation(); setPendingNavigation(null); }
        return true;
      }
      return false;
    }

    // No images — use JSON (backwards-compatible)
    const response = await fetch(`${process.env.REACT_APP_API_URL}/single-gen/save-draft`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authCtx?.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(draftPayload)
    });

    if (response.ok) {
      const result = await response.json();
      setHasUnsavedChanges(false);
      setNavigationBlocked(false);

      if (result.action === "created") {
        setCurrentDraftId(result.draft_id);
        setIsDraftMode(true);
      } else if (result.action === "updated") {
        if (result.draft_id) {
          setCurrentDraftId(result.draft_id);
        }
        setIsDraftMode(true);
      }

      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }

      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Failed to save draft:', error);
    return false;
  }
}, [formData, listingMode, existingInput, dimensionData, variantsDraftData, currentDraftId, authCtx?.token, pendingNavigation, bPlusSequence, aPlusSequence]);

// Handle page navigation within the app (popstate for back/forward buttons)
useEffect(() => {
  const handlePopState = async (e) => {
    if (hasUnsavedChanges && !showResultPopup && !isLoading) {
      e.preventDefault();
      // Push current state back to prevent navigation initially
      window.history.pushState(null, '', window.location.pathname + window.location.search);

      // Auto-save the draft silently
      await handleAutoSaveDraft();

      // Navigate back after saving
      window.history.back();
    }
  };

  // Warn before tab close / external navigation when there are unsaved changes.
  // Note: async auto-save is not possible here; browser dialog is the only guard.
  const handleBeforeUnload = (e) => {
    if (hasUnsavedChanges && !showResultPopup && !isLoading) {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome to show the dialog
    }
  };

  window.addEventListener('popstate', handlePopState);
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Prevent back button navigation when there are unsaved changes
  if (hasUnsavedChanges && !showResultPopup && !isLoading) {
    window.history.pushState(null, '', window.location.pathname + window.location.search);
  }

  return () => {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [hasUnsavedChanges, showResultPopup, isLoading, handleAutoSaveDraft]);

// Handle clicking on navigation links - auto-save on navigation
useEffect(() => {
  const handleLinkClick = async (e) => {
    if (hasUnsavedChanges && !showResultPopup && !isLoading) {
      // Check if it's a navigation link
      const link = e.target.closest('a[href]');
      if (link && link.href && !link.href.includes('#')) {
        e.preventDefault();

        // Store the intended navigation destination
        const targetUrl = link.href;
        const targetPath = link.pathname;

        // Set pending navigation before auto-save
        setPendingNavigation(() => () => {
          // Navigate to the intended destination
          if (targetPath.startsWith('/')) {
            navigate(targetPath);
          } else {
            window.location.href = targetUrl;
          }
        });

        // Auto-save the draft silently (will execute pendingNavigation)
        await handleAutoSaveDraft();
      }
    }
  };

  document.addEventListener('click', handleLinkClick);
  return () => {
    document.removeEventListener('click', handleLinkClick);
  };
}, [hasUnsavedChanges, showResultPopup, isLoading, navigate, handleAutoSaveDraft, pendingNavigation]);


const deleteDraft = async (draftId) => {
  if (!draftId) return;
  
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/single-gen/delete-draft`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authCtx?.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ draft_id: draftId })
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      setError(errData?.error || 'Failed to delete draft. You can try again from your drafts.');
    }
  } catch (error) {
    setError(error?.message || 'Failed to delete draft. You can try again from your drafts.');
  }
};



  // --- Effects (no changes needed here, keep them as they are) ---
useEffect(() => {
  if (location.state?.isNewListing !== undefined) {
    const modeFromState = location.state.isNewListing ? 'new' : 'existing';
    setListingMode(modeFromState);
  }
  
  // Handle project name from navigation state
  if (location.state?.projectName) {
    setFormData(prevData => ({
      ...prevData,
      projectName: location.state.projectName
    }));
  }
  
  // Handle initial ASIN from navigation state for auto-fetch
  if (location.state?.initialAsin) {
    setExistingInput(prevInput => ({
      ...prevInput,
      asin: location.state.initialAsin
    }));
  }
  
}, [location.state, navigate]);

// Flip the mounted ref only on real unmount (see isMountedRef declaration).
useEffect(() => {
  isMountedRef.current = true;
  return () => { isMountedRef.current = false; };
}, []);

// Prefill generator from Listing Score "Upgrade Listing" payload (no re-fetch required)
useEffect(() => {
  const STORAGE_KEY = 'listing_upgrade_payload_v1';
  let payload = location.state?.listingUpgradePayload || null;

  if (!payload) {
    try {
      payload = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      payload = null;
    }
  }

  if (!payload || !payload.listing) return;

  setListingUpgradePayload(payload);

  // Best-effort cleanup so the payload isn't accidentally reused
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Best-effort only; storage may be unavailable (e.g. private mode)
  }

  const listing = payload.listing || {};
  const asin = payload.asin || listing.asin || '';
  const title = listing.title || '';
  const description = listing.description || '';
  const bulletsIncoming = Array.isArray(listing.bullet_points) ? listing.bullet_points : [];

  // Switch to existing listing mode and jump to "Existing Listing" step
  setListingMode('existing');
  setFormStep(2);

  // Populate ASIN card + lock existing fields (consistent with "fetched ASIN" UX)
  setIsAsinDataFetched(true);
  setAsinProductData({
    asin: asin,
    title: title,
    firstImage: listing.main_image || (Array.isArray(listing.all_images) ? listing.all_images[0] : '') || '',
    categories_flat: listing.categories_flat || ''
  });

  setFormData(prev => ({
    ...prev,
    projectName: prev.projectName || asin,
    // This is what powers the "Product Description" field in the Description tab
    briefDescription: prev.briefDescription || description
  }));

  const targetBulletLen = (initialExistingInput?.bullets || []).length || 5;
  const normalizedBullets = bulletsIncoming.slice(0, targetBulletLen);
  while (normalizedBullets.length < targetBulletLen) normalizedBullets.push('');

  // Snapshot whether bullets/description are empty so user can type in empty fields
  asinFetchBulletsEmpty.current = !normalizedBullets.some(b => b.trim());
  asinFetchDescriptionEmpty.current = !description?.trim();
  setAsinBulletsLocked(normalizedBullets.some(b => b.trim()));
  setAsinDescriptionLocked(!!description?.trim());

  setExistingInput({
    ...initialExistingInput,
    asin: asin,
    title: title,
    description: description,
    bullets: normalizedBullets
  });

  // Prefill the Details tab from structured attributes carried in the payload's
  // specifications. This is the only account-type-independent source for these fields;
  // the open-account Rainforest enrichment below only layers on top and never overwrites.
  const detailsFromSpecs = mapSpecificationsToDetails(listing.specifications);
  setFormData(prev => ({
    ...prev,
    brandName: prev.brandName || detailsFromSpecs.brandName,
    color: prev.color || detailsFromSpecs.color,
    material: prev.material || detailsFromSpecs.material,
    size: prev.size || detailsFromSpecs.size,
    modelNumber: prev.modelNumber || detailsFromSpecs.modelNumber,
    eanNumber: prev.eanNumber || detailsFromSpecs.eanNumber,
    skuId: prev.skuId || detailsFromSpecs.skuId
  }));

  // Fetch richer structured details (brand/material/size/browsenode/etc) from Rainforest
  // This does NOT deduct credits (only Listing Score + Generate deduct credits).
  // Only open accounts can call Rainforest API.
  const fetchRainforestDetails = async () => {
    const accountType = authCtx?.user?.account_type || 'close';
    if (!asin || !authCtx?.token || accountType !== 'open') return;
    try {
      const response = await fetch(`${API_BASE_URL}/rainforest/lookup-asin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          asin: asin,
          amazon_domain: 'amazon.in',
          listingMode: 'existing'
        })
      });

      if (!response.ok) return;
      const data = await response.json();
      const rainforestData = data?.result;
      if (!rainforestData) return;

      // Fill details using the same mapping used by normal ASIN Lookup flow,
      // but DON'T overwrite existing title/bullets/description already set from Listing Score.
      const mappedData = mapRainforestDataToFormFields(rainforestData);
      const categoriesFlat = rainforestData.categories_flat || '';

      setFormData((prev) => ({
        ...prev,
        brandName: prev.brandName || mappedData?.formData?.brandName || '',
        category: prev.category || mappedData?.formData?.category || '',
        subCategory: prev.subCategory || mappedData?.formData?.subCategory || '',
        productType: prev.productType || mappedData?.formData?.productType || '',
        modelNumber: prev.modelNumber || mappedData?.formData?.modelNumber || '',
        skuId: prev.skuId || mappedData?.formData?.skuId || '',
        color: prev.color || mappedData?.formData?.color || '',
        material: prev.material || mappedData?.formData?.material || '',
        size: prev.size || mappedData?.formData?.size || '',
        eanNumber: prev.eanNumber || mappedData?.formData?.eanNumber || '',
        browsenode: prev.browsenode || categoriesFlat || '',
        // Keep the Listing Score description as primary; fall back to rainforest mapped description only if empty
        briefDescription: prev.briefDescription || mappedData?.formData?.briefDescription || ''
      }));

      // Resolve variation themes from the browse node's last segment
      // (e.g. "... > Bathroom Basin Taps" → "Faucet" → its variation list).
      if (categoriesFlat) {
        const enums = await fetchVariantEnumsFromBrowseNode(categoriesFlat, authCtx?.token);
        if (enums) setFormData((prev) => ({ ...prev, ...enums }));
      }
    } catch (e) {
      // Upgrade flow still works with Listing Score payload alone
    }
  };

  // Hydrate image URLs into File objects so downstream steps work immediately.
  // Guarded by isMountedRef (not a per-run flag) so the query-param sync clearing
  // location.state — which re-runs this effect — doesn't abort the in-flight fetch.
  const normalizeUrls = (urls) =>
    Array.from(
      new Set(
        (Array.isArray(urls) ? urls : [])
          .map((u) => (typeof u === 'string' ? u.trim() : ''))
          .filter(Boolean)
      )
    );

  const urls = normalizeUrls([
    listing.main_image,
    ...(Array.isArray(listing.all_images) ? listing.all_images : [])
  ]).slice(0, MAX_IMAGES);

  const hydrate = async () => {
    const results = await Promise.all(
      urls.map(async (imageUrl, i) => {
        try {
          const resp = await fetch(imageUrl);
          const blob = await resp.blob();
          const fileName = `upgrade_image_${i + 1}.jpg`;
          const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          return { file, previewUrl: imageUrl };
        } catch (e) {
          return null;
        }
      })
    );
    const hydrated = results.filter(Boolean);

    if (!isMountedRef.current) return;

    if (hydrated.length > 0) {
      setUploadedImages(hydrated);
      setBPlusSequence(hydrated);
      setAPlusSequence(hydrated);
      setSingleImage(hydrated[0]);
      setSingleImageError('');
      setProductIdentityImageError('');
    }
  };

  hydrate();
  fetchRainforestDetails();
  // Intentionally only reacts to navigation state changes.
  // No per-run cleanup: image hydration is guarded by isMountedRef so a benign
  // effect re-run (location.state cleared by the query-param sync) can't abort it.
}, [location.state]);

// Add this useEffect to handle draft loading
useEffect(() => {
  if (location.state?.loadDraft && location.state?.draftData) {
    const draftData = location.state.draftData;
    
    if (!draftData.draft_id) {
      return;
    }
    setIsDraftMode(true);
    setCurrentDraftId(draftData.draft_id);
    
    let loadedListingMode = draftData.listing_mode;
    if (!loadedListingMode && draftData.product) {
      loadedListingMode = draftData.product.is_existing ? 'existing' : 'new';
    }
    loadedListingMode = loadedListingMode || 'new';
    setListingMode(loadedListingMode);
    
    // Navigate to existing listing tab if in existing mode
    if (loadedListingMode === 'existing') {
      setFormStep(2); 
    } else {
      setFormStep(1);
    }
    
    if (draftData.product) {
      setFormData({
        ...initialNewFormData,
        ...draftData.product
      });
    }
    
    if (draftData.existing_input) {
      setExistingInput({
        ...initialExistingInput,
        ...draftData.existing_input,
        bullets: draftData.existing_input.bullets || initialExistingInput.bullets
      });
      
      // If loading a draft in existing mode with existing input data,
      // set isAsinDataFetched to true to make fields read-only
      if (loadedListingMode === 'existing') {
        const draftBullets = draftData.existing_input.bullets || initialExistingInput.bullets;
        asinFetchBulletsEmpty.current = !draftBullets?.some(b => b.trim());
        asinFetchDescriptionEmpty.current = !draftData.existing_input.description?.trim();
        setAsinBulletsLocked(draftBullets?.some(b => b.trim()));
        setAsinDescriptionLocked(!!draftData.existing_input.description?.trim());
        setIsAsinDataFetched(true);
      }
    }
    
    
    // Load dimension data
    if (draftData.dimension_data || draftData.dimensionData) {
      setDimensionData(draftData.dimension_data || draftData.dimensionData);
    }

    // Load variants tab draft data
    if (draftData.variantsDraftData) {
      setVariantsDraftData(draftData.variantsDraftData);
      setShowVariantsTab(true);
    }

    // Restore images from S3 ZIP if available
    if (draftData.images_zip_url) {
      (async () => {
        try {
          const JSZip = (await import('jszip')).default;
          const response = await fetch(draftData.images_zip_url);
          const zipBlob = await response.blob();
          const zip = await new JSZip().loadAsync(zipBlob);

          const loadImages = async (folder) => {
            const images = [];
            const prefix = `${folder}/`;
            for (const [filename, file] of Object.entries(zip.files)) {
              if (file.dir || !filename.startsWith(prefix)) continue;
              const name = filename.replace(prefix, '');
              if (!name || name.startsWith('.')) continue;
              const blob = await file.async('blob');
              const imageFile = new File([blob], name, { type: blob.type || 'image/png' });
              const previewUrl = URL.createObjectURL(blob);
              images.push({ file: imageFile, previewUrl, name });
            }
            return images;
          };

          const bPlusImages = await loadImages('bPlus');
          const aPlusImages = await loadImages('aPlus');
          const dimImages = await loadImages('dimension');

          if (bPlusImages.length > 0) setBPlusSequence(bPlusImages);
          if (aPlusImages.length > 0) setAPlusSequence(aPlusImages);
          if (dimImages.length > 0) {
            setDimensionData(prev => prev ? { ...prev, image: dimImages[0] } : null);
          }
        } catch (err) {
          console.error('Failed to restore draft images:', err);
        }
      })();
    }

    // Clear the navigation state but preserve query params (e.g. ?mode=existing)
    navigate(location.pathname + location.search, { replace: true });
  }
}, [location.state, navigate]);


  useEffect(() => {
    if (productImages.length === 0 && selectedTemplates.length === 0) { setCountError(''); return; }
    if (productImages.length > 0 && selectedTemplates.length > 0 && productImages.length !== selectedTemplates.length) {
      const targetCount = Math.max(productImages.length, selectedTemplates.length);
      const noun = productImages.length > selectedTemplates.length ? 'template(s)' : 'image(s)';
      setCountError(`Please select exactly ${targetCount} ${noun} to match image count.`);
    } else if (productImages.length === 0 && selectedTemplates.length > 0) {
      setCountError(`Please upload images to use the selected templates.`);
    }
    else { setCountError(''); }
  }, [productImages, selectedTemplates]);

  useEffect(() => {
    return () => {
      uploadedImages.forEach(img => {
        if (img.previewUrl && img.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
    };
  }, [uploadedImages]);

  // Keep refs current so the unmount effect below always revokes the latest URLs.
  useEffect(() => { bPlusSequenceRef.current = bPlusSequence; }, [bPlusSequence]);
  useEffect(() => { aPlusSequenceRef.current = aPlusSequence; }, [aPlusSequence]);

  // Revoke bPlus/aPlus blob URLs on unmount (e.g. URLs created during draft restore).
  // Using refs avoids the stale-closure problem that [] deps would cause.
  useEffect(() => {
    return () => {
      bPlusSequenceRef.current.forEach(img => {
        if (img?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(img.previewUrl);
      });
      aPlusSequenceRef.current.forEach(img => {
        if (img?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(img.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      // Clean up dimension image only if it's different from singleImage.
      // deps include dimensionData and singleImage so the closure captures
      // current values, not the null values captured at mount with [].
      if (dimensionData?.image?.previewUrl) {
        if (!singleImage || dimensionData.image.previewUrl !== singleImage.previewUrl) {
          URL.revokeObjectURL(dimensionData.image.previewUrl);
        }
      }
    };
  }, [dimensionData, singleImage]);


  useEffect(() => {
    if (showResultPopup && generatedResult?.showResultsOnly) {
      const timer = setTimeout(() => {
        setShowTutorialStep(true);
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, [showResultPopup, generatedResult]);

  // Sync showResultPopup with context. Reset to false on unmount so a stale
  // `true` doesn't leak into the bulk page (which would hide the themes
  // sidebar/panel via DashboardLayout's `!showResultPopup` gate).
  useEffect(() => {
    setContextShowResultPopup(showResultPopup);
    return () => { setContextShowResultPopup(false); };
  }, [showResultPopup, setContextShowResultPopup]);

  // Auto-open themes panel when first entering infographics tab (only once per tab visit)
  const hasAutoOpenedRef = useRef(false);
  const prevInfographicsTabRef = useRef(false);
  
  useEffect(() => {
    // Infographics step depends on whether Variants tab is enabled.
    // - Without Variants: Infographics is step 4 (new) / step 5 (existing)
    // - With Variants:    Variants is step 4 (new) / step 5 (existing), Infographics shifts to step 5 (new) / step 6 (existing)
    const infographicsStep = showVariantsTab
      ? (listingMode === 'new' ? 5 : 6)
      : (listingMode === 'new' ? 4 : 5);

    const isCurrentlyInfographicsTab = formStep === infographicsStep && !showResultPopup;
    
    // If we just entered the infographics tab and haven't auto-opened yet
    if (isCurrentlyInfographicsTab && !prevInfographicsTabRef.current && !hasAutoOpenedRef.current) {
      setIsThemesPanelOpen(true);
      hasAutoOpenedRef.current = true;
    }
    
    // Reset auto-open flag when leaving infographics tab
    if (!isCurrentlyInfographicsTab && prevInfographicsTabRef.current) {
      hasAutoOpenedRef.current = false;
    }
    
    prevInfographicsTabRef.current = isCurrentlyInfographicsTab;
  }, [formStep, listingMode, showResultPopup, showVariantsTab, setIsThemesPanelOpen]);


  // --- Theme selection helpers (batched via startTransition to avoid multiple renders) ---
  const applyThemeMapping = (theme) => {
    startTransition(() => {
      if (!theme) {
        setSelectedTemplates([]);
        setProductImages([]);
        setSelectedTemplatesWithImages([]);
        setSelectedAPlusTemplate(null);
        setAPlusTemplateImage(null);
        return;
      }

      // 1) Feature templates (B+ images)
      const featureTemplates = theme.feature_templates || [];
      if (featureTemplates.length > 0) {
        setSelectedTemplates(featureTemplates);

        if (bPlusSequence && bPlusSequence.length > 0) {
          const featureTemplateCount = featureTemplates.length;
          const bPlusLength = bPlusSequence.length;
          const productImagesArray = [];
          for (let i = 0; i < featureTemplateCount; i++) {
            const sourceImage = bPlusSequence[i % bPlusLength];
            productImagesArray.push({
              file: sourceImage.file,
              previewUrl: sourceImage.previewUrl,
              imagePath: sourceImage.imagePath
            });
          }
          setProductImages(productImagesArray);
        } else {
          setProductImages([]);
        }
      } else {
        setSelectedTemplates([]);
        setProductImages([]);
      }

      // 2) Primary image and dimensions from first B+ image
      if (bPlusSequence && bPlusSequence[0]) {
        const firstBPlus = bPlusSequence[0];
        const imageData = {
          file: firstBPlus.file,
          previewUrl: firstBPlus.previewUrl,
          ...(firstBPlus.imagePath ? { imagePath: firstBPlus.imagePath } : {})
        };
        setSingleImage(imageData);
        setSingleImageError('');
        setDimensionData(prev => {
          const base = prev || { length: '', breadth: '', isComplete: false };
          const updated = { ...base, image: imageData };
          return { ...updated, isComplete: !!(updated.length && updated.breadth) };
        });
      }

      // 3) Application template: send template id + images array
      if (theme.application_template) {
        const appImages = (bPlusSequence || []).map((img) => ({
          file: img.file,
          previewUrl: img.previewUrl,
          imagePath: img.imagePath
        }));
        setSelectedTemplatesWithImages([{
          id: theme.application_template,
          images: appImages,
          applicationHeaders: {}
        }]);
      } else {
        setSelectedTemplatesWithImages([]);
      }

      // 4) A+ template and image from A+ sequence
      if (theme.aplus_theme) {
        setSelectedAPlusTemplate({
          id: theme.aplus_theme,
          name: `Theme ${theme.aplus_theme.replace('theme', '')}`,
          slides: []
        });
        if (aPlusSequence && aPlusSequence[0]) {
          const firstAPlus = aPlusSequence[0];
          setAPlusTemplateImage({
            file: firstAPlus.file,
            previewUrl: firstAPlus.previewUrl,
            ...(firstAPlus.imagePath ? { imagePath: firstAPlus.imagePath } : {})
          });
          setAPlusImageError('');
        } else {
          setAPlusTemplateImage(null);
        }
      } else {
        setSelectedAPlusTemplate(null);
        setAPlusTemplateImage(null);
      }
    });
  };

  const handleThemeSelect = useCallback((theme) => {
    setSelectedTheme(theme);
    setError(null);
    applyThemeMapping(theme);
  }, [bPlusSequence, aPlusSequence]); // Include dependencies used by applyThemeMapping

  // Sync selectedTheme to context
  useEffect(() => {
    setContextSelectedTheme(selectedTheme);
  }, [selectedTheme, setContextSelectedTheme]);

  // Sync handleThemeSelect to context - use ref to avoid infinite loop
  const handleThemeSelectRef = useRef(handleThemeSelect);
  useEffect(() => {
    handleThemeSelectRef.current = handleThemeSelect;
  }, [handleThemeSelect]);

  useEffect(() => {
    setContextHandleThemeSelect(() => (theme) => handleThemeSelectRef.current(theme));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only set once, use ref to get latest function

  // Re-apply theme mapping if sequences change
  useEffect(() => {
    if (selectedTheme) {
      applyThemeMapping(selectedTheme);
    }
  }, [bPlusSequence, aPlusSequence, selectedTheme]);

  const handleListingModeChange = (event) => {
    const newMode = event.target.value;
    setListingMode(newMode);

    const currentProjectName = formData.projectName;
    // Reset relevant states
    setError(null); setGeneratedResult(null); setShowResultPopup(false);
    setImageError(''); setCountError('');
    setFormData({
      ...initialNewFormData,
      projectName: currentProjectName || '' // Preserve project name or default to empty string
    });
    setExistingInput(initialExistingInput);
    setIsAsinDataFetched(false); 
    
    
    setDimensionData(null);
    setProductImages([]); setSelectedTemplates([]);
    setSingleImage(null); setSingleImageError('');
    setSelectedTemplatesWithImages([]);
    
    // Reset A+ template data
    setSelectedAPlusTemplate(null);
    setAPlusTemplateImage(null);
    setAPlusImageError('');
    
    setFormStep(1); // Reset to first step when mode changes
    setCurrentCarouselStep(1); // Reset carousel step as well
  };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prevData => ({ ...prevData, [name]: value }));
      setError(null);
      if (invalidFields.has(name)) {
        setInvalidFields(prev => {
          const next = new Set(prev);
          next.delete(name);
          return next;
        });
        let current = e.target.parentElement;
        while (current) {
          if (Array.from(current.classList || []).some(cls => cls.includes('inputContainer') || cls.includes('inputWrapper'))) {
            current.classList.remove(styles.fieldInvalid);
            break;
          }
          current = current.parentElement;
        }
      }
    };

    const handleExistingInputChange = (e) => {
      if (isAsinDataFetched) {
        const { name: fieldName } = e.target;
        // Title is always locked after ASIN fetch
        if (fieldName === 'title') return;
        const isBulletField = fieldName.startsWith('bullet-');
        const isDescriptionField = fieldName === 'description';
        if (isBulletField && asinBulletsLocked) return;
        if (isDescriptionField && asinDescriptionLocked) return;
      }
      
      const { name, value } = e.target;
      setError(null);
      if (name.startsWith('bullet-')) {
        const index = parseInt(name.split('-')[1], 10);
        setExistingInput(prevData => {
          const newBullets = [...prevData.bullets];
          if (index >= 0 && index < newBullets.length) { newBullets[index] = value; }
          const updated = { ...prevData, bullets: newBullets };
          if (newBullets.some(b => b.trim()) && invalidFields.size > 0) {
            setInvalidFields(prev => {
              const next = new Set(prev);
              Array.from(prev).filter(f => f.startsWith('bullet-')).forEach(f => {
                next.delete(f);
                document.querySelectorAll(`input[name="${f}"]`).forEach(input => {
                  let current = input.parentElement;
                  while (current) {
                    if (Array.from(current.classList || []).some(cls => cls.includes('formGroup'))) {
                      current.classList.remove(styles.fieldInvalid);
                      break;
                    }
                    current = current.parentElement;
                  }
                });
              });
              return next;
            });
          }
          return updated;
        });
      } else {
        setExistingInput(prevData => ({ ...prevData, [name]: value }));
        if (invalidFields.has(name)) {
          setInvalidFields(prev => {
            const next = new Set(prev);
            next.delete(name);
            return next;
          });
          let current = e.target.parentElement;
          while (current) {
            if (Array.from(current.classList || []).some(cls => cls.includes('formGroup'))) {
              current.classList.remove(styles.fieldInvalid);
              break;
            }
            current = current.parentElement;
          }
        }
      }
    };

    // Map fetched children of a parent ASIN into the variants draft: resolve the
    // real variation theme against the product type's themes when available
    // (falling back to the Color/Size heuristic), fill per-child values + SKUs,
    // and download each child's listing images into its variant slot.
    const applyFetchedChildrenToVariants = (childrenData, themes) => {
      try {
        const children = childrenData;

        // Value of a variation attribute for a child. Theme parts are SP-API codes
        // (SET_NAME, COLOR_NAME, NUMBER_OF_ITEMS…); values live in the child's
        // specifications under the lowercased name, with color/size/material also
        // available as top-level fields.
        const getChildVariantAttr = (child, themePart) => {
          const key = themePart.trim().toLowerCase();
          const base = key.replace(/_name$/, '').replace(/_type$/, '');
          const specs = Array.isArray(child.specifications) ? child.specifications : [];
          const fromSpecs = (k) => {
            const hit = specs.find((s) => s && typeof s.name === 'string' && s.name.toLowerCase() === k && s.value != null && String(s.value).trim());
            return hit ? String(hit.value).trim() : '';
          };
          return fromSpecs(key) || fromSpecs(base) || String(child[base] || '').trim();
        };

        // Pick the theme whose attributes are present on every child, preferring
        // themes that vary across children and uniquely identify each child.
        const pickTheme = () => {
          let best = null;
          for (const theme of themes || []) {
            const parts = String(theme).split('/').map((p) => p.trim()).filter(Boolean);
            if (parts.length < 1 || parts.length > 3) continue;
            const valuesPerPart = parts.map((part) => children.map((c) => getChildVariantAttr(c, part)));
            if (valuesPerPart.some((vals) => vals.some((v) => !v))) continue;
            const varying = valuesPerPart.filter((vals) => new Set(vals.map((v) => v.toLowerCase())).size > 1).length;
            if (varying === 0) continue;
            const combos = new Set(children.map((_, i) => valuesPerPart.map((vals) => vals[i].toLowerCase()).join('||'))).size;
            const score = (combos === children.length ? 100 : 0) + varying * 10 - parts.length;
            if (!best || score > best.score) best = { theme: String(theme), parts, score };
          }
          return best;
        };

        let picked = pickTheme();
        if (!picked) {
          // Heuristic fallback (previous behaviour) from direct color/size fields
          const uniqueColors = [...new Set(children.map((c) => c.color || '').filter(Boolean))];
          const uniqueSizes = [...new Set(children.map((c) => c.size || '').filter(Boolean))];
          if (uniqueColors.length > 1 && uniqueSizes.length > 1) picked = { theme: 'Color/Size', parts: ['Color', 'Size'] };
          else if (uniqueSizes.length > 1) picked = { theme: 'Size', parts: ['Size'] };
          else if (uniqueColors.length > 1) picked = { theme: 'Color', parts: ['Color'] };
          else if (uniqueSizes.length > 0) picked = { theme: 'Size', parts: ['Size'] };
        }

        let variantType;
        let level1Values;
        let variants;
        // Where each child's images belong: l3 === null → row-level (images.level2)
        const imageSlots = [];
        const emptyLevel3 = () => [{ value: '', sku: '', images: [] }];

        if (picked) {
          const { parts } = picked;
          variantType = picked.theme;
          level1Values = [...new Set(children.map((c) => getChildVariantAttr(c, parts[0])))];
          variants = level1Values.map((l1Val, l1Idx) => {
            const group = children.map((c, idx) => ({ c, idx })).filter(({ c }) => getChildVariantAttr(c, parts[0]) === l1Val);
            if (parts.length < 3) {
              return group.map(({ c, idx }, row) => {
                imageSlots.push({ childIndex: idx, l1: l1Idx, row, l3: null });
                return { level2: parts.length === 2 ? getChildVariantAttr(c, parts[1]) : '', level3Fields: emptyLevel3(), sku: c.sku_id || '', images: {} };
              });
            }
            const level2Vals = [...new Set(group.map(({ c }) => getChildVariantAttr(c, parts[1])))];
            return level2Vals.map((l2Val, row) => {
              const level3Fields = group
                .filter(({ c }) => getChildVariantAttr(c, parts[1]) === l2Val)
                .map(({ c, idx }, l3) => {
                  imageSlots.push({ childIndex: idx, l1: l1Idx, row, l3 });
                  return { value: getChildVariantAttr(c, parts[2]), sku: c.sku_id || '', images: [] };
                });
              return { level2: l2Val, level3Fields, sku: '', images: {} };
            });
          });
        } else {
          // No usable attributes at all: child SKUs/ASINs as level1 (previous behaviour)
          variantType = 'Other';
          level1Values = children.map((c) => c.sku_id || c.modelNumber || '');
          variants = children.map((c, idx) => {
            imageSlots.push({ childIndex: idx, l1: idx, row: 0, l3: null });
            return [{ level2: '', level3Fields: emptyLevel3(), sku: c.sku_id || '', images: {} }];
          });
        }

        setVariantsDraftData({ variantType, level1Values, variants });
        setShowVariantsTab(true);

        // Download each child's listing images (max 5 per SKU) into its variant slot
        const fetchChildImages = async () => {
          const results = await Promise.all(
            imageSlots.map(async (slot) => {
              const child = children[slot.childIndex];
              const urls = (child.all_images || []).slice(0, 5);
              const images = [];
              for (let i = 0; i < urls.length; i++) {
                try {
                  const response = await fetch(urls[i]);
                  const blob = await response.blob();
                  const fileName = `${child.sku_id || 'variant'}_${i + 1}.jpg`;
                  images.push({ file: new File([blob], fileName, { type: blob.type || 'image/jpeg' }), previewUrl: urls[i] });
                } catch (error) {
                }
              }
              return { slot, images };
            })
          );
          setVariantsDraftData((prev) => {
            if (!prev || prev.variantType !== variantType || !Array.isArray(prev.variants)) return prev;
            const next = prev.variants.map((rows) => rows.map((v) => ({ ...v })));
            for (const { slot, images } of results) {
              if (images.length === 0) continue;
              const row = next[slot.l1]?.[slot.row];
              if (!row) continue;
              if (slot.l3 === null) {
                row.images = { ...(row.images || {}), level2: images };
              } else if (Array.isArray(row.level3Fields) && row.level3Fields[slot.l3]) {
                row.level3Fields = row.level3Fields.map((f, k) => (k === slot.l3 ? { ...f, images } : f));
              }
            }
            return { ...prev, variants: next };
          });
        };
        if (imageSlots.length > 0) fetchChildImages();
      } catch (error) {
      }
    };

    // ASIN lookup handler
    const handleAsinDataFetched = (productData, searchedAsin = null, childrenData = [], isParent = false) => {
      if (!productData) {
        setError("No product data received from ASIN lookup");
        return;
      }

      try {
        // Prefer the ASIN from the response (the search text may be a SKU),
        // falling back to the ref / searched value as before
        const asinFromRef = asinLookupFormRef.current?.asin?.trim();
        const asinValue = productData.asin || productData.ASIN || productData.request_metadata?.asin || asinFromRef || searchedAsin || productData.modelNumber || productData.model_number || '';
        const categoriesFlat = productData.categories_flat || '';
        setAsinProductData({
          asin: asinValue,
          title: productData.title || productData.Title || productData.existing_title || '',
          firstImage: productData.main_image || (productData.all_images && productData.all_images[0]) || '',
          categories_flat: categoriesFlat
        });

        // Map the Rainforest API data to our form fields
        const mappedData = mapRainforestDataToFormFields(productData);
        
        if (!mappedData) {
          setError("Failed to map product data to form fields");
          return;
        }

        // Determine if we should switch to existing mode based on the data
        if (mappedData.shouldSwitchToExistingMode && listingMode !== 'existing') {
          setListingMode('existing');
        }

        // On re-fetch, overwrite ASIN-sourced fields instead of preserving stale data
        const isRefetch = isAsinDataFetched;
        if (isRefetch) {
          setDimensionData(null);
        }

        // Update form data
        setFormData((prev) => ({
          ...prev,
          projectName: isRefetch ? asinValue : (prev.projectName || asinValue),
          brandName: isRefetch ? mappedData.formData.brandName : (prev.brandName || mappedData.formData.brandName),
          category: isRefetch ? mappedData.formData.category : (prev.category || mappedData.formData.category),
          subCategory: isRefetch ? mappedData.formData.subCategory : (prev.subCategory || mappedData.formData.subCategory),
          productType: isRefetch ? mappedData.formData.productType : (prev.productType || mappedData.formData.productType),
          modelNumber: isRefetch ? asinValue : (prev.modelNumber || asinValue),
          skuId: isRefetch ? mappedData.formData.skuId : (prev.skuId || mappedData.formData.skuId),
          color: isRefetch ? mappedData.formData.color : (prev.color || mappedData.formData.color),
          material: isRefetch ? mappedData.formData.material : (prev.material || mappedData.formData.material),
          size: isRefetch ? mappedData.formData.size : (prev.size || mappedData.formData.size),
          eanNumber: isRefetch ? mappedData.formData.eanNumber : (prev.eanNumber || mappedData.formData.eanNumber),
          keywords: isRefetch ? mappedData.formData.keywords : (prev.keywords || mappedData.formData.keywords),
          feature1: isRefetch ? mappedData.formData.feature1 : (prev.feature1 || mappedData.formData.feature1),
          feature2: isRefetch ? mappedData.formData.feature2 : (prev.feature2 || mappedData.formData.feature2),
          feature3: isRefetch ? mappedData.formData.feature3 : (prev.feature3 || mappedData.formData.feature3),
          feature4: isRefetch ? mappedData.formData.feature4 : (prev.feature4 || mappedData.formData.feature4),
          feature5: isRefetch ? mappedData.formData.feature5 : (prev.feature5 || mappedData.formData.feature5),
          briefDescription: isRefetch ? mappedData.formData.briefDescription : (prev.briefDescription || mappedData.formData.briefDescription),
          browsenode: categoriesFlat || prev.browsenode || '',
        }));

        setExistingInput((prev) => ({
          ...prev,
          asin: asinValue,
          title: isRefetch ? mappedData.existingInput.title : (prev.title || mappedData.existingInput.title),
          description: isRefetch ? mappedData.existingInput.description : (prev.description || mappedData.existingInput.description),
          bullets: mappedData.existingInput.bullets || prev.bullets
        }));

        // Resolve variation themes for the variants step from the browse node's last
        // segment (e.g. "... > Bathroom Basin Taps" → "Faucet" → its variation list).
        // A parent's children are mapped into the variants draft afterwards, so the
        // real variation theme (e.g. SET_NAME/NUMBER_OF_ITEMS) can be matched.
        const hasChildren = isParent && childrenData && childrenData.length > 0;
        if (categoriesFlat) {
          fetchVariantEnumsFromBrowseNode(categoriesFlat, authCtx?.token)
            .then((enums) => {
              if (enums) setFormData((prev) => ({ ...prev, ...enums }));
              if (hasChildren) applyFetchedChildrenToVariants(childrenData, enums?.productTypeFilteredEnums || []);
            })
            .catch(() => {
              if (hasChildren) applyFetchedChildrenToVariants(childrenData, []);
            });
        } else if (hasChildren) {
          applyFetchedChildrenToVariants(childrenData, []);
        }

        // Fetch ASIN images — replace on re-fetch, append on first fetch
        if (productData.all_images && productData.all_images.length > 0) {
          const fetchAsinImages = async () => {
            const asinImages = [];

            for (let i = 0; i < productData.all_images.length; i++) {
              const imageUrl = productData.all_images[i];
              try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const fileName = `asin_image_${i + 1}.jpg`;
                const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

                asinImages.push({
                  file: file,
                  previewUrl: imageUrl
                });
              } catch (error) {
              }
            }

            if (asinImages.length > 0) {
              if (isRefetch) {
                setUploadedImages(asinImages);
                setBPlusSequence(asinImages);
                setAPlusSequence(asinImages);
              } else {
                setUploadedImages(prev => [...prev, ...asinImages]);
                setBPlusSequence(prev => [...prev, ...asinImages]);
                setAPlusSequence(prev => [...prev, ...asinImages]);
              }
            }
          };

          fetchAsinImages();
        } else if (isRefetch) {
          // New ASIN has no images — clear old ones
          setUploadedImages([]);
          setBPlusSequence([]);
          setAPlusSequence([]);
        }
        
        // Snapshot whether bullets/description are empty so user can type in empty fields
        const fetchedBullets = mappedData.existingInput.bullets || existingInput.bullets;
        asinFetchBulletsEmpty.current = !fetchedBullets?.some(b => b.trim());
        asinFetchDescriptionEmpty.current = !(mappedData.existingInput.description || existingInput.description)?.trim();
        setAsinBulletsLocked(fetchedBullets?.some(b => b.trim()));
        setAsinDescriptionLocked(!!(mappedData.existingInput.description || existingInput.description)?.trim());

        // Mark that ASIN data has been fetched
        setIsAsinDataFetched(true);
        
        
        // Clear any previous errors
        setError(null);
        
        if (listingMode === 'existing' && formStep === 1) {
          setTimeout(() => {
            setFormStep(2);
          }, 300);
        }
        
        // Advance to next step if we were waiting for fetch to complete
        if (shouldAdvanceAfterAsinFetch) {
          setShouldAdvanceAfterAsinFetch(false);
          const totalSteps = listingMode === 'new' ? 4 : 5;
          setTimeout(() => {
            setFormStep(prev => {
              if (prev < totalSteps) {
                return prev + 1;
              }
              return prev;
            });
          }, 100);
        }
        
        // Scroll to the form sections
        setTimeout(() => {
          const formElement = document.querySelector(`.${styles.formCard}`);
          if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
        
        // Track the successful ASIN lookup if posthog is available
        if (posthog) {
          posthog.capture('asin_lookup_success', {
            timestamp: new Date().toISOString(),
          });
        }
      } catch (err) {
        setError("Failed to process product data: " + (err.message || "Unknown error"));
      }
    };


    const submitGeneration = async (e) => {
      if (e) e.preventDefault();

      // Proactive credits guard: prompt (saving progress as a draft on "Buy Credits")
      // before generating when the balance is below this generation's cost.
      if (!hasEnoughCreditsOrPrompt(authCtx?.user?.credits, SINGLE_GEN_COST, { onBuyCredits: handleAutoSaveDraft })) {
        setError('Insufficient credits. Please purchase more credits to continue.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Require a theme selection from the left sidebar before generating infographics
      if (!selectedTheme || !selectedTheme.id) {
        setError("Please select a theme from the sidebar to continue");
        setIsThemesPanelOpen(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const bPlusMissingImages = selectedTemplates.length > 0 && bPlusSequence.length === 0;
      const aPlusMissingImages = selectedAPlusTemplate && aPlusSequence.length === 0;

      if (bPlusMissingImages && aPlusMissingImages) {
        setError("Please upload at least one image in both the sections to generate content.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (bPlusMissingImages) {
        setError("Please upload at least one image in the B+ section to generate content.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (aPlusMissingImages) {
        setError("Please upload at least one image in the A+ section to generate content.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

  // Validate application templates 
  for (let template of selectedTemplatesWithImages) {
    const templateLabel = template.name ?? template.id ?? 'Application template';
    if (template.error) {
      setError(`${templateLabel}: ${template.error}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
  }

      // Require dimensions image
      if (!dimensionData || !dimensionData.dimensionImage || !dimensionData.dimensionImage.file) {
        setError("Please upload a dimensions image in the Product Dimensions section.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Validate form data
      const validation = validateFormData(formData, productImages, selectedTemplates, countError);
      if (!validation.isValid) {
        setError(validation.errorMessage);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll up to show error
        return;
      }

      // Remind the user if they haven't removed the background on any image yet.
      // Skipped once they've chosen "Continue anyway" (bypass ref).
      const anyBackgroundRemoved = [...(bPlusSequence || []), ...(aPlusSequence || [])]
        .some(img => img?.backgroundRemoved);
      if (!anyBackgroundRemoved && !bgReminderBypassRef.current) {
        setShowBgReminder(true);
        return;
      }

      setIsLoading(true);
      setError(null);
      setGeneratedResult(null);
      setShowResultPopup(false);

      try {
        // Prepare data for submission
        const browsenodeValue = formData.browsenode?.trim() || asinProductData?.categories_flat?.trim() || '';
        const { endpoint, payload, formDataApi } = prepareSubmissionData(
          formData,
          existingInput,
          productImages,
          selectedTemplates,
          listingMode,
          singleImage,
          selectedTemplatesWithImages,
          dimensionData,
          selectedAPlusTemplate,
          aPlusSequence,
          null,
          uploadedImages,
          bPlusSequence,
          browsenodeValue,
          selectedFonts,
          listingUpgradePayload,
          variantsDraftData,
          zoomAreas
        );

        // Append lifestyle image — last B+ slot per the renderer's layout.
        // Falls back to theme_slide_counts.json for themes not in THEME_CONFIG.
        if (bPlusSequence && bPlusSequence.length > 0 && selectedTheme?.id) {
          const themeConfig = THEME_CONFIG[selectedTheme.id];
          const itemsToRender = themeConfig?.bplus?.length
            ?? themeSlideCounts[selectedTheme.id]?.bplus
            ?? 0;
          if (itemsToRender > 0) {
            const lifestyleImgIndex = (itemsToRender - 1) % bPlusSequence.length;
            const lifestyleImg = bPlusSequence[lifestyleImgIndex];
            if (lifestyleImg?.file) {
              formDataApi.append('lifestyle_image', lifestyleImg.file, lifestyleImg.file.name);
            }
          }
        }

        // Set up authorization header
        const token = authCtx?.token;
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
        }

        // Flag tutorial-driven generations so the backend can grant the
        // one-time free run for first-time users. Backend enforces the
        // single-use atomically — the flag itself is not a credit bypass.
        if (tutorial?.active) {
          formDataApi.append('is_tutorial', 'true');
        }

        // Make API call
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: headers,
          body: formDataApi
        });

        // Handle response
        if (!response.ok) {
          const errorText = await handleApiError(response);
          throw new Error(errorText);
        }

        // Process successful response
        const resultData = await response.json();

        // Validate expected structure
        if (resultData.results && Array.isArray(resultData.results) && resultData.results.length > 0) {
          setGeneratedResult(resultData.results[0]);
          setShowResultPopup(true);
          setError(null);

          // Delete draft if we were editing one and generation was successful
          if (isDraftMode && currentDraftId) {
            await deleteDraft(currentDraftId);
          }

          setIsDraftMode(false);
          setCurrentDraftId(null);
          setHasUnsavedChanges(false);

          const route = window.location.pathname;
          if (posthog) {
            posthog.capture('generate_feature_used', {
              feature_used: route,
              timestamp: new Date().toISOString(),
            });
          }

          // Update user credits if available — backend returns credits.remaining
          const newCreditValue = resultData?.credits?.remaining;
          if (typeof newCreditValue === 'number' && authCtx?.updateUserCredit) {
            authCtx.updateUserCredit(newCreditValue);
          }
        } else {
          throw new Error("Received an unexpected response format from the server.");
        }
      } catch (err) {
        const msg = err.message || '';
        if (/insufficient credit|no_credits/i.test(msg)) {
          // Save current progress as a draft if the user clicks "Buy Credits".
          triggerLowCreditsModal({ onBuyCredits: handleAutoSaveDraft });
        }
        setError(msg || "An unexpected error occurred. Please check your connection or try again later.");
        setGeneratedResult(null);
        setShowResultPopup(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setIsLoading(false);
      }
    };

    // Rage clicks on Generate would otherwise fire one paid generation per click —
    // the loading state only guards once React has re-rendered.
    const handleSubmit = useSingleFlight(submitGeneration);

    // "Remove Background" on the reminder: close it and open the Remove
    // Background modal inside the infographics step via the signal counter.
    // Mark the reminder as acknowledged so it won't reappear on the next
    // Generate even if the user closes the modal without removing anything.
    const handleBgReminderRemove = () => {
      setShowBgReminder(false);
      bgReminderBypassRef.current = true;
      setOpenRemoveBgSignal(prev => prev + 1);
    };

    // "Continue anyway": skip the reminder for the rest of the session and
    // proceed straight to generation.
    const handleBgReminderContinue = () => {
      setShowBgReminder(false);
      bgReminderBypassRef.current = true;
      handleSubmit();
    };


    const isFormValid = () => {
      // 1. Check if primary image is selected
      if (!singleImage) {
        return { isValid: false, reason: "Primary image is required" };
      }

        // 2. Check if at least 1 product image is uploaded for infographics
        const requiredImageCount = 1;

        if (productImages.length < requiredImageCount) {
          return { 
            isValid: false, 
            reason: `Please upload at least ${requiredImageCount} product images for infographics` 
          };
        }

             // 5. Validate each selected application template (generate mode only; backend generates, no upload required)
       for (let template of selectedTemplatesWithImages) {
         const templateLabel = template.name ?? template.id ?? 'Application template';
         if (template.error) {
           return { isValid: false, reason: `${templateLabel}: ${template.error}` };
         }
       }

      // 6. Validate A+ content: template and at least one image must be selected
      if (!selectedAPlusTemplate || !aPlusSequence || aPlusSequence.length === 0) {
        return {
          isValid: false,
          reason: "Please select an A+ template and upload at least one image"
        };
      }

      // 6b. Validate B+ sequence: at least one image must be uploaded
      if (!bPlusSequence || bPlusSequence.length === 0) {
        return {
          isValid: false,
          reason: "Please upload at least one image in the B+ section"
        };
      }

      // 7. Require dimensions image
      if (!dimensionData || !dimensionData.dimensionImage || !dimensionData.dimensionImage.file) {
        return {
          isValid: false,
          reason: "Please upload a dimensions image in the Product Dimensions section"
        };
      }

      // 8. Validate basic form data (existing validation)
      const validation = validateFormData(formData, productImages, selectedTemplates, countError);
      if (!validation.isValid) {
        return { isValid: false, reason: validation.errorMessage };
      }

      return { isValid: true, reason: "" };
    };

      const handleAPlusTemplateSelect = (template) => {
      setSelectedAPlusTemplate(template);
      setError(null);
    };

    const handleAPlusImageChange = (file) => {
      setAPlusImageError('');
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      
      if (!allowedTypes.includes(file.type)) {
        setAPlusImageError(`File "${file.name}" is not a valid image type.`);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setAPlusImageError(`File "${file.name}" is too large (max 5MB).`);
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setAPlusTemplateImage({ file: file, previewUrl: previewUrl });
      setError(null);
    };

    const removeAPlusImage = () => {
      if (aplusTemplateImage) {
        setAPlusTemplateImage(null);
      }
      setAPlusImageError('');
      setError(null);
    };


    // Add this new function to handle content updates from regeneration
    const handleContentUpdated = (updatedContent) => {
      // Update the generatedResult with the new content
      setGeneratedResult(prevResult => {
        const newResult = {
          ...prevResult,
          claude_output: {
            ...prevResult.claude_output,
            ...updatedContent
          }
        };
        return newResult;
      });
    };

    // Get total form steps based on listing mode
    const getTotalFormSteps = () => {
      const baseSteps = listingMode === 'new' ? 4 : 5;
      return showVariantsTab ? baseSteps + 1 : baseSteps;
    };


    // Single source of truth for step validation; returns valid flag and set of invalid field names
    const getFormStepValidation = (step) => {
      const invalidFields = new Set();
      let valid = false;
      if (listingMode === 'new') {
        switch (step) {
          case 1:
            if (productIdentityRef.current?.getUploadedImagesCount) {
              valid = productIdentityRef.current.getUploadedImagesCount() > 0;
            } else {
              valid = !!(bPlusSequence?.length > 0);
            }
            break;
          case 2:
            valid = !!(
              formData.brandName?.trim() &&
              formData.productType?.trim() &&
              formData.modelNumber?.trim() &&
              formData.skuId?.trim()
            );
            if (!formData.brandName?.trim()) invalidFields.add('brandName');
            if (!formData.productType?.trim()) invalidFields.add('productType');
            if (!formData.modelNumber?.trim()) invalidFields.add('modelNumber');
            if (!formData.skuId?.trim()) invalidFields.add('skuId');
            break;
          case 3:
            valid = !!(formData.briefDescription?.trim() && formData.feature1?.trim());
            if (!formData.briefDescription?.trim()) invalidFields.add('briefDescription');
            if (!formData.feature1?.trim()) invalidFields.add('feature1');
            break;
          case 4:
            valid = showVariantsTab ? isVariantsValid : !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            break;
          case 5:
            valid = !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            break;
          default:
            break;
        }
      } else {
        switch (step) {
          case 1:
            valid = true;
            break;
          case 2:
            // Title is always required; bullets and description are optional (user can leave them empty)
            valid = !!(existingInput.title?.trim());
            if (!existingInput.title?.trim()) invalidFields.add('title');
            break;
          case 3:
            valid = !!(
              formData.brandName?.trim() &&
              formData.modelNumber?.trim() &&
              formData.skuId?.trim()
            );
            if (!formData.brandName?.trim()) invalidFields.add('brandName');
            if (!formData.modelNumber?.trim()) invalidFields.add('modelNumber');
            if (!formData.skuId?.trim()) invalidFields.add('skuId');
            break;
          case 4:
            valid = !!(formData.briefDescription?.trim() && formData.feature1?.trim());
            if (!formData.briefDescription?.trim()) invalidFields.add('briefDescription');
            if (!formData.feature1?.trim()) invalidFields.add('feature1');
            break;
          case 5:
            valid = showVariantsTab ? isVariantsValid : !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            break;
          case 6:
            valid = !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            break;
          default:
            break;
        }
      }
      return { valid, invalidFields };
    };

    const isFormStepValid = (step) => getFormStepValidation(step).valid;

    // Surface the validation feedback shown when `step` is incomplete: field
    // highlights plus the step-specific image / key-features errors. Shared by the
    // Next button and tab-name navigation so the two can't drift apart.
    const surfaceStepValidationErrors = (step, stepInvalidFields) => {
      setInvalidFields(stepInvalidFields);
      if (listingMode === 'new' && step === 1) {
        setProductIdentityImageError('Please upload at least one image to continue');
      }
      if ((listingMode === 'new' && step === 3) || (listingMode === 'existing' && step === 4)) {
        if (!formData.feature1?.trim()) setKeyFeaturesError(true);
      }
    };

    // Handle tab-name navigation. Backward jumps are always allowed. Forward jumps
    // validate every step between the current one and the target; if any is invalid,
    // land on the first invalid step and surface the same highlights the Next button
    // shows, instead of silently doing nothing.
    const handleTabSelect = (step) => {
      if (step === formStep) return;
      if (step < formStep) {
        setFormStep(step);
        return;
      }
      for (let s = formStep; s < step; s++) {
        const { valid, invalidFields: stepInvalidFields } = getFormStepValidation(s);
        if (!valid) {
          if (s !== formStep) setFormStep(s);
          surfaceStepValidationErrors(s, stepInvalidFields);
          return;
        }
      }
      setInvalidFields(new Set());
      setFormStep(step);
    };

    // Handle form step navigation
    const handleFormStepNext = async () => {
      const totalSteps = getTotalFormSteps();
      const { valid, invalidFields } = getFormStepValidation(formStep);

      if (!valid) {
        surfaceStepValidationErrors(formStep, invalidFields);
        return;
      }
      setInvalidFields(new Set());
      // Clear Product Identity image error when validation passes
      if (listingMode === 'new' && formStep === 1) {
        setProductIdentityImageError('');
      }
      // Clear Key Features error when validation passes
      if ((listingMode === 'new' && formStep === 3) || (listingMode === 'existing' && formStep === 4)) {
        if (formData.feature1?.trim()) {
          setKeyFeaturesError(false);
        }
      }
      
      // If on step 1 in existing mode and an ASIN or catalogue SKU is entered,
      // always fetch (re-fetch allowed)
      if (listingMode === 'existing' && formStep === 1 && asinLookupFormRef.current) {
        const trimmedAsin = asinLookupFormRef.current.asin?.trim();
        const isSkuMatch = asinLookupFormRef.current.isCatalogueSku?.(trimmedAsin);
        if (trimmedAsin && (trimmedAsin.length >= 10 || isSkuMatch) && !asinLookupFormRef.current.isLoading) {
          setShouldAdvanceAfterAsinFetch(true);
          await asinLookupFormRef.current.fetchProduct({ preventDefault: () => {} });
          return;
        }
      }
      
      
      // If on step 1 (Product Identity) and in new mode, extract details first
      if (formStep === 1 && listingMode === 'new' && productIdentityRef.current) {
        // Check if user provided any input in Product Identity field
        const userInput = productIdentityRef.current.getDescription ? productIdentityRef.current.getDescription() : '';
        
        // If no input provided, skip API call and just navigate
        if (!userInput) {
          if (formStep < totalSteps && isFormStepValid(formStep)) {
            setFormStep(prev => prev + 1);
          }
          return;
        }
        
        // Only call API if there's actual input
        setIsLoading(true);
        setError(null);
        try {
          const result = await productIdentityRef.current.extractProductDetails(true);
          
          if (result && result.success) {
            setTimeout(() => {
              setFormStep(2);
              setIsLoading(false);
            }, 500);
            return;
          } else {
            setError(result?.error || 'Failed to extract product details. You can continue manually.');
            setIsLoading(false);
            if (formStep < totalSteps && isFormStepValid(formStep)) {
              setFormStep(prev => prev + 1);
            }
            return;
          }
        } catch (error) {
          setError('An error occurred while extracting product details. You can continue manually.');
          setIsLoading(false);
          if (formStep < totalSteps && isFormStepValid(formStep)) {
            setFormStep(prev => prev + 1);
          }
          return;
        }
      }
      
      // Normal navigation for other steps
      if (formStep < totalSteps && isFormStepValid(formStep)) {
        setFormStep(prev => prev + 1);
        // Reset carousel to step 1 when entering the infographics step (existing mode only)
        // Note: when Variants tab is enabled, Infographics shifts by +1 step.
        const infographicsStep = showVariantsTab ? 6 : 5;
        if (listingMode === 'existing' && formStep === infographicsStep - 1) {
          setCurrentCarouselStep(1);
        }
      }
    };

    const handleFormStepPrev = () => {
      if (formStep > 1) {
        setFormStep(prev => prev - 1);
        // Reset carousel to step 1 when leaving the infographics step
        const infographicsStepNew = showVariantsTab ? 5 : 4;
        const infographicsStepExisting = showVariantsTab ? 6 : 5;
        if ((listingMode === 'new' && formStep === infographicsStepNew) || (listingMode === 'existing' && formStep === infographicsStepExisting)) {
          setCurrentCarouselStep(1);
        }
      }
    };

    const onEnableVariantsTab = useCallback(() => {
      setShowVariantsTab(true);
      const variantsStep = listingMode === 'new' ? 4 : 5;
      setFormStep(variantsStep);
    }, [listingMode, setShowVariantsTab, setFormStep]);

    const onNoVariants = useCallback(() => {
      setShowVariantsTab(false);
      setVariantsDraftData(null);
      const descriptionStep = listingMode === 'new' ? 3 : 4;
      setFormStep(descriptionStep);
    }, [listingMode, setShowVariantsTab, setVariantsDraftData, setFormStep]);

    const onRemoveAsinData = useCallback(() => {
      setAsinProductData(null);
      setIsAsinDataFetched(false);
      setExistingInput({
        ...initialExistingInput,
        title: '',
        description: '',
        bullets: initialExistingInput.bullets.map(() => '')
      });
    }, [setAsinProductData, setIsAsinDataFetched, setExistingInput]);

    const tutorial = useTutorial();
    const tutorialDisableAsinInput = tutorial.active && tutorial.step === 5;

    const singleListingFormValue = useMemo(() => ({
      formRef,
      listingMode,
      formStep,
      formData,
      existingInput,
      dimensionData,
      productIdentityRef,
      asinLookupFormRef,
      asinInputElementRef,
      formStepNextButtonRef,
      skuIdFieldRef,
      keywordsFieldRef,
      bannedKeywordsFieldRef,
      briefDescriptionRef,
      keyFeaturesRef,
      howIsUsedRef,
      productDimensionsRef,
      tutorialDisableAsinInput,
      generateButtonRef,
      handleSubmit,
      handleChange,
      handleExistingInputChange,
      handleFormStepNext,
      handleProductIdentityImages,
      handleAsinDataFetched,
      setFormData,
      setFormStep,
      setExistingInput,
      setDimensionData,
      isLoading,
      isAsinLookupLoading,
      isAsinDataFetched,
      currentAsinValue,
      countError,
      bPlusSequence,
      aPlusSequence,
      setBPlusSequence,
      setAPlusSequence,
      zoomAreas,
      setZoomAreas,
      openRemoveBgSignal,
      productIdentityImageError,
      keyFeaturesError,
      asinProductData,
      asinResetKey,
      setShouldAdvanceAfterAsinFetch,
      setIsAsinLookupLoading,
      setCurrentAsinValue,
      selectedTemplatesWithImages,
      isFormValid,
      getTotalFormSteps,
      initialExistingInput,
      disableAsinAutoFetch,
      showVariantsTab,
      invalidFields,
      asinBulletsLocked,
      asinDescriptionLocked,
      variantsDraftData,
      setVariantsDraftData,
      onVariantsValidationChange: setIsVariantsValid,
      isVariantsValid,
      variantsInternalStep,
      onVariantsInternalStepChange: setVariantsInternalStep,
      onEnableVariantsTab,
      onNoVariants,
      onRemoveAsinData,
    }), [
      listingMode, formStep, formData, existingInput, dimensionData,
      handleSubmit, handleChange, handleExistingInputChange, handleFormStepNext,
      handleProductIdentityImages, handleAsinDataFetched,
      isLoading, isAsinLookupLoading, isAsinDataFetched, currentAsinValue,
      countError, bPlusSequence, aPlusSequence, openRemoveBgSignal,
      productIdentityImageError, keyFeaturesError, asinProductData, asinResetKey,
      selectedTemplatesWithImages, isFormValid, getTotalFormSteps,
      showVariantsTab, invalidFields, asinBulletsLocked, asinDescriptionLocked,
      variantsDraftData, isVariantsValid, variantsInternalStep,
      onEnableVariantsTab, onNoVariants, onRemoveAsinData,
    ]);

  const handleTutorialSampleSelect = useCallback((asin) => {
    const entry = sampleAsins[asin];
    if (!entry) return;
    // Mark ASIN as fetched FIRST so AsinLookupForm's auto-fetch (which fires
    // when initialAsin changes) is gated off — otherwise a real Rainforest
    // lookup would race our sample data and call handleAsinDataFetched again
    // ~2s later, reverting the user back to the Existing Listing tab.
    setIsAsinDataFetched(true);
    setExistingInput((prev) => ({ ...prev, asin }));
    setIsAsinLookupLoading(true);
    tutorial.setStep(6);
    setTimeout(() => {
      handleAsinDataFetched(entry.formData, asin);
      setIsAsinLookupLoading(false);
    }, 5000);
  }, [handleAsinDataFetched, setExistingInput, setIsAsinDataFetched, setIsAsinLookupLoading, tutorial]);

  const tutorialSampleList = useMemo(
    () => Object.values(sampleAsins).map((entry) => entry.display),
    []
  );

  // Advance tutorial when the user moves to the next form step.
  useEffect(() => {
    if (!tutorial.active || listingMode !== 'existing') return;
    if (tutorial.step === 6 && formStep === 3) tutorial.setStep(7);
    if (tutorial.step === 8 && formStep === 4) tutorial.setStep(9);
    if (tutorial.step === 10 && formStep === 5) tutorial.setStep(11);
  }, [tutorial, formStep, listingMode]);

  // On Existing Listing → Details transition, vertically center the form card
  // so the first fields land mid-viewport instead of clipping at the top.
  const prevFormStepRef = useRef(formStep);
  useEffect(() => {
    const prev = prevFormStepRef.current;
    prevFormStepRef.current = formStep;
    if (listingMode === 'existing' && prev === 2 && formStep === 3) {
      setTimeout(() => {
        const formElement = document.querySelector(`.${styles.formCard}`);
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [formStep, listingMode]);

  // Step 11: auto-open the Themes panel so the user sees the theme grid.
  useEffect(() => {
    if (tutorial.active && tutorial.step === 11 && !isThemesPanelOpen) {
      setIsThemesPanelOpen(true);
    }
  }, [tutorial, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Step 11: poll for the second theme card to mount, then store it as a target.
  const [tutorialThemeNode, setTutorialThemeNode] = useState(null);
  useEffect(() => {
    if (!tutorial.active || tutorial.step !== 11) {
      setTutorialThemeNode(null);
      return undefined;
    }
    let cancelled = false;
    let timer;
    const tryFind = () => {
      if (cancelled) return;
      const cards = document.querySelectorAll('[data-tutorial-theme-card]');
      if (cards.length >= 2) {
        setTutorialThemeNode(cards[1]);
      } else {
        timer = setTimeout(tryFind, 120);
      }
    };
    tryFind();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tutorial.active, tutorial.step, isThemesPanelOpen]);

  const tutorialThemeRef = useMemo(() => ({ current: tutorialThemeNode }), [tutorialThemeNode]);

  // Step 11 ↔ 12: keep the tutorial in sync with whether the user is looking
  // at the theme grid or the theme preview. We poll every frame so:
  //  - Forward: as soon as `[data-tutorial-select-theme]` mounts (preview
  //    view opens) we advance 11 → 12.
  //  - Backward: when the user hits Back in the preview header the button
  //    unmounts; we revert 12 → 11 so the spotlight returns to the theme
  //    cards instead of pointing at the now-detached button.
  const [tutorialSelectThemeNode, setTutorialSelectThemeNode] = useState(null);
  useEffect(() => {
    if (!tutorial.active || (tutorial.step !== 11 && tutorial.step !== 12)) {
      setTutorialSelectThemeNode(null);
      return undefined;
    }
    let cancelled = false;
    let rafId;
    const tick = () => {
      if (cancelled) return;
      const btn = document.querySelector('[data-tutorial-select-theme]');
      if (btn) {
        setTutorialSelectThemeNode((prev) => (prev === btn ? prev : btn));
        if (tutorial.step === 11) {
          setTutorialThemeNode(null);
          tutorial.setStep(12);
        }
      } else if (tutorial.step === 12) {
        setTutorialSelectThemeNode(null);
        tutorial.setStep(11);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [tutorial, isThemesPanelOpen]);

  const tutorialSelectThemeRef = useMemo(
    () => ({ current: tutorialSelectThemeNode }),
    [tutorialSelectThemeNode]
  );

  // Step 12 → 13: when the user picks a theme (selectedTheme is set), close
  // the Themes panel ourselves and advance to the Product Dimensions tooltip.
  useEffect(() => {
    if (tutorial.active && tutorial.step === 12 && selectedTheme) {
      if (isThemesPanelOpen) setIsThemesPanelOpen(false);
      tutorial.setStep(13);
    }
  }, [tutorial, selectedTheme, isThemesPanelOpen, setIsThemesPanelOpen]);

  const handleTutorialFillSampleDimensions = useCallback(() => {
    if (setDimensionData) {
      setDimensionData((prev) => ({
        ...(prev || {}),
        length: (prev && prev.length) || '20',
        breadth: (prev && prev.breadth) || '15',
        height: (prev && prev.height) || '10',
        units: (prev && prev.units) || 'cm',
        isComplete: true,
      }));
    }
    tutorial.setStep(14);
  }, [setDimensionData, tutorial]);

  const handleTutorialFillSampleSku = useCallback(() => {
    setFormData((prev) => {
      const brand = (prev.brandName || 'Sample').replace(/\s+/g, '');
      const sampleKeywords = 'wireless headphones, bluetooth, over ear, premium audio, noise cancelling';
      return {
        ...prev,
        skuId: `${brand}P1`,
        keywords: prev.keywords && prev.keywords.trim() ? prev.keywords : sampleKeywords,
      };
    });
    // Highlight the keywords immediately for 3s, then advance to the Next button.
    setTutorialFillPhase('highlight');
    setTimeout(() => {
      setTutorialFillPhase('idle');
      tutorial.setStep(8);
      formStepNextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 3000);
  }, [setFormData, tutorial]);

  // Step 7: switch from the SKU tooltip to the Keywords tooltip 1s after the
  // user stops typing in the SKU input.
  const [skuTypedSettled, setSkuTypedSettled] = useState(false);
  useEffect(() => {
    if (!tutorial.active || tutorial.step !== 7) {
      setSkuTypedSettled(false);
      return undefined;
    }
    if (!formData.skuId || !formData.skuId.trim()) {
      setSkuTypedSettled(false);
      return undefined;
    }
    const t = setTimeout(() => setSkuTypedSettled(true), 1000);
    return () => clearTimeout(t);
  }, [formData.skuId, tutorial.active, tutorial.step]);

  // Step 7 → 8: once the Keywords tooltip is up (skuTypedSettled), auto-fill
  // sample keywords, leave them on screen for 3s, then advance to the
  // Next-button tooltip.
  useEffect(() => {
    if (!tutorial.active || tutorial.step !== 7 || !skuTypedSettled) return undefined;
    setFormData((prev) => {
      if (prev.keywords && prev.keywords.trim()) return prev;
      return {
        ...prev,
        keywords: 'wireless headphones, bluetooth, over ear, premium audio, noise cancelling',
      };
    });
    const t = setTimeout(() => {
      tutorial.setStep(8);
      formStepNextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 4000);
    return () => clearTimeout(t);
  }, [tutorial, skuTypedSettled, setFormData]);

  const handleTutorialFillSampleDescription = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      feature1: prev.feature1 || 'Premium quality build',
      feature2: prev.feature2 || 'Easy setup and use',
      feature3: prev.feature3 || 'Backed by manufacturer warranty',
      feature4: prev.feature4 || 'Designed for everyday durability',
      feature5: prev.feature5 || 'Trusted by thousands of customers',
      application1: prev.application1 || 'Home',
      application2: prev.application2 || 'Office',
      application3: prev.application3 || 'Travel',
    }));
    // Highlight the key features for 3s, then advance to the Next button.
    setTutorialFillPhase('highlight');
    setTimeout(() => {
      setTutorialFillPhase('idle');
      tutorial.setStep(10);
      formStepNextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 3000);
  }, [setFormData, tutorial]);

  return (
    <>
      {isLoading && !(formStep === 1 && listingMode === 'new') && <LoadingOverlay />}
      <div className={styles.pageContainer + (showResultPopup ? ' ' + styles.showingResults : '')} >
      {showResultPopup && generatedResult ? (
        <SingleResultsCard
          resultData={generatedResult}
          isInline={true}
          onContentUpdated={handleContentUpdated}
          authToken={authCtx?.token}
        />
      ) : null}
      {showTutorialStep && (
        <div className={styles.tutorialTooltip}>
          <div className={styles.tutorialContent}>
            <FaArrowLeft className={styles.tutorialArrow} />
            <span className={styles.tutorialText}>
              Go to Dashboard
            </span>
            <button
              onClick={() => setShowTutorialStep(false)}
              className={styles.tutorialCloseButton}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {!showResultPopup && (
        <>
          {/* Form Card */}
          <div className={`${styles.formCard} ${(isThemesPanelOpen || isFontsPanelOpen) ? styles.formCardWithPanel : ''}`}>
            {/* Display general form error prominently */}
            {error && !isLoading && <p className={`${styles.errorText} ${styles.formError}`}>{error}</p>}

            {/* Step Navigation Tabs */}
            <StepNavigationTabs
              listingMode={listingMode}
              formStep={formStep}
              setFormStep={setFormStep}
              isAsinDataFetched={isAsinDataFetched}
              isFormStepValid={isFormStepValid}
              onTabSelect={handleTabSelect}
              isThemesPanelOpen={isThemesPanelOpen}
              isFontsPanelOpen={isFontsPanelOpen}
              showVariantsTab={showVariantsTab}
            />

            <SingleListingFormProvider value={singleListingFormValue}>
              <FormSteps />
            </SingleListingFormProvider>
          </div>



        </>
      )}
    </div>

    <BgReminderModal
      isOpen={showBgReminder}
      onClose={() => setShowBgReminder(false)}
      onRemoveBackground={handleBgReminderRemove}
      onContinue={handleBgReminderContinue}
    />

    {tutorial.active && tutorial.step === 5 && listingMode === 'existing' && (
      <TutorialTooltip
        show
        targetRef={asinInputElementRef}
        title="Paste an Amazon ASIN"
        description="Enter the 10-character ASIN of the listing you want to optimize. We will extract all relevant live data — including title, bullets, and images."
        placement="bottom"
        showDots={false}
        cursorAnchor="tooltip"
        clickHereLabel="Select a product"
      >
        <TutorialSampleCards
          samples={tutorialSampleList}
          onSelect={handleTutorialSampleSelect}
        />
      </TutorialTooltip>
    )}

    {tutorial.active && tutorial.step === 6 && listingMode === 'existing' && formStep === 2 && (
      <TutorialTooltip
        show
        targetRef={formStepNextButtonRef}
        title="Move to the editor"
        description="Click Next to start editing. Most fields will already be filled from the ASIN — review and tweak as you like."
        placement="top-center"
        showDots={false}
        showCursor={false}
        showClickHere={false}
        scrollIntoView
      />
    )}

    {tutorial.active && tutorial.step === 7 && listingMode === 'existing' && formStep === 3 && !skuTypedSettled && (
      <TutorialTooltip
        show
        targetRef={skuIdFieldRef}
        title="Brand and identifiers"
        description="Brand, SKU and EAN may be pre-filled from the ASIN. Confirm or adjust them — these affect listing eligibility."
        placement="left"
        showDots={false}
      />
    )}

    {tutorial.active && tutorial.step === 7 && listingMode === 'existing' && formStep === 3 && skuTypedSettled && (
      <TutorialTooltip
        show
        targetRef={keywordsFieldRef}
        extraTargetRefs={[bannedKeywordsFieldRef]}
        title="Sample keywords added"
        description="We've added a sample set of keywords for you — these get weighted into your title and bullets so the listing ranks better. You can edit them anytime."
        placement="top"
        showDots={false}
      />
    )}

    {tutorial.active && tutorial.step === 8 && listingMode === 'existing' && formStep === 3 && (
      <TutorialTooltip
        show
        targetRef={formStepNextButtonRef}
        title="Move to the next step"
        description="Review the details and click Next when you're ready."
        placement="top-center"
        showDots={false}
      />
    )}

    {tutorial.active && tutorial.step === 9 && listingMode === 'existing' && formStep === 4 && tutorialFillPhase === 'idle' && (
      <TutorialTooltip
        show
        targetRef={keyFeaturesRef}
        title="Quick pitch + key features"
        description="In a sentence or two, tell us what the product does and why someone should buy it. Then add up to 5 selling points — these become your bullet points."
        placement="bottom"
        showDots={false}
        onNext={handleTutorialFillSampleDescription}
        nextLabel="Add Sample Details"
        scrollIntoView
      />
    )}

    {tutorial.active && tutorial.step === 9 && listingMode === 'existing' && formStep === 4 && tutorialFillPhase === 'highlight' && (
      <TutorialTooltip
        show
        targetRef={keyFeaturesRef}
        title="Key features added"
        description="These become the bullet points on your listing."
        placement="bottom"
        showDots={false}
        showCursor={false}
        showClickHere={false}
      />
    )}

    {tutorial.active && tutorial.step === 10 && listingMode === 'existing' && formStep === 4 && (
      <TutorialTooltip
        show
        targetRef={formStepNextButtonRef}
        title="Ready for images"
        description="Click Next to move to the image step where you'll upload your B+ hero photos."
        placement="top-center"
        showDots={false}
        scrollIntoView
      />
    )}

    {tutorial.active && tutorial.step === 11 && tutorialThemeNode && (
      <TutorialTooltip
        show
        targetRef={tutorialThemeRef}
        title="Choose a Theme"
        description="Select a theme that best matches your product style and enhances its presentation"
        placement="right"
        showDots={false}
        scrollIntoView
      />
    )}

    {tutorial.active && tutorial.step === 12 && tutorialSelectThemeNode && (
      <TutorialTooltip
        show
        targetRef={tutorialSelectThemeRef}
        title="Pick Your Theme"
        description="If all templates look good, go ahead and select the one that best fits your product"
        placement="right"
        showDots={false}
      />
    )}

    {tutorial.active && tutorial.step === 13 && listingMode === 'existing' && formStep === 5 && (
      <TutorialTooltip
        show
        targetRef={productDimensionsRef}
        title="Confirm product dimensions"
        description="Length × breadth × height. We use this for the dimension graphic on your A+ content. Most brands fill this once and forget."
        placement="right"
        showDots={false}
        onNext={handleTutorialFillSampleDimensions}
        nextLabel="Add Sample Dimensions"
        scrollIntoView
      />
    )}
    </>
  );
};

export default SingleListingGeneratorPage;